import {
  CAlert,
  CButton,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CLoadingButton,
} from '@coreui/react-pro'
import React, { useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import ArticleService from 'src/service/ArticleService'
import ToastComponent from 'src/components/common/TaostComponent'
import ImageDropzone from 'src/components/common/ImageDropzone'
import useSponsorQuillModules from '../Sponsor/useSponsorQuillModules'

const SUPPORTED_FORMATS = ['image/jpg', 'image/png', 'image/jpeg', 'image/gif']
const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/gif'
const MAX_IMAGE_SIZE = 700 * 440 // bytes, matches the API's existing limit

const validationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required').max(50, '50 Character Limit is allowed.'),
  image: Yup.mixed()
    .nullable()
    .required('Image is required')
    .test('size', 'File size too large, max file size is 300KB', (value) =>
      value ? value.size <= MAX_IMAGE_SIZE : true,
    )
    .test(
      'type',
      'Invalid file format selection',
      (value) => !value || SUPPORTED_FORMATS.includes(value?.type),
    ),
})

const AddForm = ({ visible, onClose, fetchUsers }) => {
  const [loader, setLoader] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])
  const keepOpenRef = useRef(false)
  const quillRef = useRef()
  const modules = useSponsorQuillModules(quillRef)

  const formik = useFormik({
    initialValues: {
      title: '',
      link_type: '',
      external_link: '',
      description: '',
      image: null,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data, actions) => {
      const formData = new FormData()
      formData.append('title', data.title)
      formData.append('type', data.link_type)
      formData.append('description', data.description)
      formData.append('external_link', data.external_link)
      formData.append('image', data.image)

      setLoader(true)
      ArticleService.saveArticle(formData)
        .then((res) => {
          if (res.status === 200) {
            setErrorMessages([])
            fetchUsers?.()
            ToastComponent(res.message, 'success')
            resetFields(actions)
            if (!keepOpenRef.current) {
              onClose()
            }
          } else {
            const errors =
              res.errors && Array.isArray(res.errors)
                ? res.errors
                : [res.message || 'Something went wrong.']
            setErrorMessages(errors)
          }
        })
        .catch(() => {
          ToastComponent('Something went wrong. Please try again.', 'error')
        })
        .finally(() => setLoader(false))
    },
  })

  const resetFields = (actions) => {
    actions.resetForm({
      values: { title: '', link_type: '', external_link: '', description: '', image: null },
    })
  }

  const handleClose = () => {
    resetFields(formik)
    setErrorMessages([])
    onClose()
  }

  const submitForm = (keepOpen) => {
    keepOpenRef.current = keepOpen
    formik.handleSubmit()
  }

  return (
    <COffcanvas
      placement="end"
      visible={visible}
      onHide={handleClose}
      backdrop
      style={{ width: 560 }}
    >
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Add Article</COffcanvasTitle>
          <div className="text-medium-emphasis small">
            Create an article members will see in the app, with its image and description.
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={handleClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        {errorMessages.length > 0 && (
          <div className="error-list">
            {errorMessages.map((error, index) => (
              <CAlert color="danger" key={index}>
                {error}
              </CAlert>
            ))}
          </div>
        )}
        <CForm className="row g-3 flex-grow-1 align-content-start" onSubmit={formik.handleSubmit}>
          <CCol xs={12}>
            <CFormLabel htmlFor="title">Title *</CFormLabel>
            <CFormInput
              id="title"
              name="title"
              placeholder="Title"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.errors.title && formik.touched.title)}
            />
            {formik.errors.title && formik.touched.title && (
              <CFormFeedback invalid>{formik.errors.title}</CFormFeedback>
            )}
          </CCol>
          <CCol xs={12}>
            <CFormLabel htmlFor="image">Image *</CFormLabel>
            <ImageDropzone
              accept={ACCEPTED_IMAGE_TYPES}
              hint="JPG, PNG or GIF. 350px x 220px recommended."
              minHeight={140}
              file={formik.values.image}
              error={formik.touched.image ? formik.errors.image : null}
              onChange={(selected) => {
                formik.setTouched({ ...formik.touched, image: true })
                formik.setFieldValue('image', selected)
              }}
            />
          </CCol>

          <CCol xs={12}>
            <hr className="mt-1 mb-0" />
            <h6 className="fw-bold mb-0 mt-3">Optional Link</h6>
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="external_link">External Link</CFormLabel>
            <CFormInput
              id="external_link"
              name="external_link"
              placeholder="External Link"
              value={formik.values.external_link}
              onChange={formik.handleChange}
              aria-label="external_link"
            />
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="link_type">Open Link</CFormLabel>
            <CFormSelect
              id="link_type"
              name="link_type"
              aria-label="Open Link"
              value={formik.values.link_type}
              onChange={formik.handleChange}
            >
              <option value="">Please select type</option>
              <option value={1}>Same Window</option>
              <option value={2}>New Tab</option>
            </CFormSelect>
          </CCol>

          <CCol xs={12}>
            <CFormLabel htmlFor="description">Short Description</CFormLabel>
            <div className="at-editor">
              <ReactQuill
                theme="snow"
                ref={quillRef}
                value={formik.values.description}
                onChange={(content) => formik.setFieldValue('description', content)}
                modules={modules}
              />
            </div>
          </CCol>
        </CForm>
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={handleClose}>
          Cancel
        </CButton>
        <div className="d-flex gap-2">
          <CLoadingButton
            color="secondary"
            variant="outline"
            loading={loader}
            onClick={() => submitForm(true)}
          >
            Save &amp; Add Another
          </CLoadingButton>
          <CLoadingButton color="primary" loading={loader} onClick={() => submitForm(false)}>
            Create Article
          </CLoadingButton>
        </div>
      </div>
    </COffcanvas>
  )
}

export default AddForm
